import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { fetchNewsBySlug, fetchAllNews } from "../api/newsApi";
import "../css/NewsDetail.css";
import BackToTop from "../components/BackToTop";

const NewsDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [recentNews, setRecentNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);
  const [processedHtml, setProcessedHtml] = useState("");

  // Load article and recent news
  useEffect(() => {
    const loadArticleAndRecent = async () => {
      try {
        const [articleData, allNews] = await Promise.all([
          fetchNewsBySlug(slug),
          fetchAllNews(),
        ]);
        setArticle(articleData);
        setRecentNews(allNews.filter((item) => item.Slug !== slug).slice(0, 5));
      } catch (err) {
        console.error(err);
        setError(err.message || "Article not found");
      } finally {
        setLoading(false);
      }
    };
    if (slug) loadArticleAndRecent();
    else {
      setLoading(false);
      setError("Invalid article slug");
    }
  }, [slug]);

  // Group consecutive images and prepare HTML
  useEffect(() => {
    if (!article) return;

    let html = article.Paragraphs;
    // Find sequences of consecutive <img> tags (with optional whitespace) and wrap them
    const imgSequenceRegex = /((?:<img[^>]*>)(?:\s*(?:<img[^>]*>))+)/gi;
    html = html.replace(imgSequenceRegex, (match) => {
      const imgTags = match.match(/<img[^>]*>/gi) || [];
      if (imgTags.length >= 2) {
        const wrapper = document.createElement("div");
        wrapper.className = "side-by-side-group";
        wrapper.style.cssText =
          "display: flex; flex-wrap: wrap; gap: 1rem; margin: 1rem auto; justify-content: center;";
        imgTags.forEach((imgTag) => {
          const item = document.createElement("div");
          item.style.cssText = "flex: 1 1 0; min-width: 100px;";
          item.innerHTML = imgTag;
          wrapper.appendChild(item);
        });
        const tempDiv = document.createElement("div");
        tempDiv.appendChild(wrapper);
        return tempDiv.innerHTML;
      }
      return match;
    });

    const sanitized = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "u",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
        "a",
        "img",
        "div",
        "span",
      ],
      ALLOWED_ATTR: [
        "href",
        "src",
        "alt",
        "title",
        "width",
        "height",
        "class",
        "style",
      ],
    });
    setProcessedHtml(sanitized);
  }, [article]);

  // After the HTML is rendered, fix single images centering
  useEffect(() => {
    if (!contentRef.current) return;
    const container = contentRef.current;
    const singleImages = container.querySelectorAll(
      "img:not(.side-by-side-group img)",
    );
    singleImages.forEach((img) => {
      if (
        img.parentElement &&
        img.parentElement.classList &&
        img.parentElement.classList.contains("single-image-wrapper")
      )
        return;
      const wrapper = document.createElement("div");
      wrapper.style.textAlign = "center";
      wrapper.style.margin = "1.5rem 0";
      wrapper.classList.add("single-image-wrapper");
      const parent = img.parentNode;
      parent.insertBefore(wrapper, img);
      wrapper.appendChild(img);
      if (parent && parent.tagName === "P" && parent.children.length === 0) {
        parent.remove();
      }
    });
  }, [processedHtml]);

  if (loading) return <div className="loading-spinner">Loading article...</div>;
  if (error || !article) {
    return (
      <>
        <Header />
        <div className="news-detail-error">
          <p>Article not found.</p>
          <Link to="/news">Back to News</Link>
        </div>
        <Footer />
      </>
    );
  }

  const title = article.Title;
  const date = article.Date;
  // const mainImage = article.MainImage; // no longer used – removed to avoid duplicate

  return (
    <>
      <Header />
      <div className="news-detail-page">
        <div className="news-detail-breadcrumb">
          <Link to="/">Home</Link> &gt; <Link to="/news">News</Link> &gt;{" "}
          <span className="current">{title}</span>
        </div>

        <div className="news-detail-container">
          <div className="news-detail-main">
            <div className="news-detail-header">
              <h1 className="news-detail-title">{title}</h1>
              <div className="news-detail-date">{date}</div>
            </div>

            {/* Main image removed – no longer shown at top. Use editor to insert image inside content. */}

            <div
              ref={contentRef}
              className="news-detail-content"
              dangerouslySetInnerHTML={{ __html: processedHtml }}
            />
          </div>

          <aside className="news-detail-sidebar">
            <h2 className="sidebar-title">Recent News</h2>
            <ul className="recent-news-list">
              {recentNews.map((item) => (
                <li key={item.Id}>
                  <Link to={`/news/${item.Slug}`}>
                    {item.MainImage && (
                      <img src={item.MainImage} alt={item.Title} />
                    )}
                    <span>{item.Title.substring(0, 60)}…</span>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
      <BackToTop />
      <Footer />
    </>
  );
};

export default NewsDetail;
